import { PortalWithdrawalTableProps } from '@/components/portal-modules/PortalWithdrawalTable';
import { defaultColumn, qtyFormatterConfig } from '@/config/config';
import { convertToTableRow } from '@/helpers/portalDepositHelper';
import { portalWithdrawalColumns } from '@/helpers/portalWithdrawalHelper';
import { useAppSelector } from '@/state/hooks';
import { selectTablePagination } from '@/state/reducers/tableSettingSlice';
import { DepositWithdrawalRequestHistory } from '@/types/sso';
import {
    ColumnDef,
    filterFns,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useDateFormatter, useNumberFormatter } from 'react-aria';
import { useDebouncedValue } from './useDebouncedValue';
import useLanguage from './useLanguage';
import { fuzzyGlobalFilter } from './useOpenPositionTable';

export interface PortalWithdrawalRow extends DepositWithdrawalRequestHistory {
    tableUpdatedAt: string;
    tableAmount: string;
    tableMethod: string;
    tableStatus: string;
}

interface UsePortalWithdrawalTableProps extends PortalWithdrawalTableProps {}

const usePortalWithdrawalTable = (props: UsePortalWithdrawalTableProps) => {
    const { data, setOffset } = props;

    const pagination = useAppSelector(selectTablePagination);

    const lang = useLanguage();
    const formatQty = useNumberFormatter(qtyFormatterConfig);
    const formatDate = useDateFormatter();

    const [sorting, setSorting] = useState<SortingState>([]);
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const [columnSizing, setColumnSizing] = useState<Record<string, number>>({});

    const columns: ColumnDef<PortalWithdrawalRow>[] = useMemo(() => {
        return portalWithdrawalColumns({ lang, formatQty, formatDate });
    }, [lang, formatQty]);

    const formattedPortalWithdrawals: PortalWithdrawalRow[] = useMemo(() => {
        if (data) return data.data.map((request) => convertToTableRow(request, formatQty, formatDate));
        return [];
    }, [data]);

    const tableInstance = useReactTable({
        data: formattedPortalWithdrawals,
        columns,
        state: { globalFilter, columnSizing, sorting },
        defaultColumn: { ...defaultColumn, filterFn: filterFns.includesString },
        pageCount: Math.ceil((data?.total || 0) / pagination.pageSize),
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        globalFilterFn: (row, columnId, filterValue) => {
            return fuzzyGlobalFilter(row, columnId, filterValue, formatDate);
        },
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        getPaginationRowModel: getPaginationRowModel(),
        onColumnSizingChange: setColumnSizing,
        onSortingChange: setSorting,
        enableRowSelection: true,
        manualPagination: true
    });

    useEffect(() => {
        const pageIndex = tableInstance.getState().pagination.pageIndex;
        setOffset(pageIndex * pagination.pageSize);
    }, [tableInstance.getState().pagination.pageIndex]);

    const [debouncedTableInstance] = useDebouncedValue(tableInstance, 500);

    return { debouncedTableInstance, columnSizing, sorting, formattedPortalWithdrawals, globalFilter };
};

export default usePortalWithdrawalTable;
